// @need-translate
import invert from 'lodash/invert';
import {AgeGrade, OnIssue} from '../graphql/generated/types';
import {CategoryTypeVo} from './shared/api/nicolas/vo/enums/CategoryTypeVo.enum';
import {IS_CLIENT_SIDE} from './shared/utils/constants';
import {TextTKey} from './type';

export const AGE_GRADE_TO_NUMBER_MAP: {
  [key in AgeGrade]: number;
} = {
  [AgeGrade.All]: 0,
  [AgeGrade.Fifteen]: 15,
  [AgeGrade.Eighteen]: 18,
  [AgeGrade.Nineteen]: 19,
};

export const NUMBER_TO_AGE_GRADE_MAP = invert(AGE_GRADE_TO_NUMBER_MAP) as {
  [key: number]: AgeGrade;
};

export const SCHEME_CLOSE_WEBVIEW = 'kakaopage://close/webview';

/**
 * https://kakaopagecorp.slack.com/archives/G01KVHU8M35/p1661482770680409
 * 빌링웹에서는 보안정책에 의해 하나하나의 스킴 유효값을 확인하고 있습니다.
 * Next에서 앱내 웹뷰 종료시 사용하게 된 kakaopage://close/webview 스킴은 유효하지 않은 스킴으로 판단되어 빌링웹에서 문제가 발생하고 있으므로
 * 빌링웹에 웹뷰 종료 스킴을 넘길때에는 아래의 os별 구 스킴을 사용해야 합니다.
 */
export const BILLING_WEB_ANDROID_LEGACY_SCHEME_CLOSE_WEBVIEW =
  'kakaopage://close';
export const BILLING_WEB_IOS_LEGACY_SCHEME_CLOSE_WEBVIEW = 'app://page/close';

export const IOS_KAKAOPAGE_STORE_URI =
  'itms-apps://itunes.apple.com/app/id616643813';

export const SESSION_EXPIRED_PATH = '/error/session-expired';

export const STYLE_MAIN_MAX_WIDTH_PC_NUMBER = 720;

/**
 * 카카오 웹툰에서 가져온 passive가능을 체크하는 로직입니다.
 * 참고 : https://github.com/kakaoent/kakaowebtoon-web/blob/sandbox/src/common/constant.ts
 */
// passive 속성값 feature detection
let supportsPassive = false;
try {
  const opts = Object.defineProperty({}, 'passive', {
    get() {
      supportsPassive = true;
    },
  });
  if (IS_CLIENT_SIDE) {
    window.addEventListener('testPassive', null as any, opts);
    window.removeEventListener('testPassive', null as any, opts);
  }
} catch (e) {}
// 가능하다면 touchstart 이벤트를 붙일때 passive 속성값을 준다
export const PASSIVE_TOUCH_IF_POSSIBLE: AddEventListenerOptions | boolean =
  supportsPassive ? {passive: true} : false;
export const DISABLE_PASSIVE_TOUCH_IF_POSSIBLE:
  | AddEventListenerOptions
  | boolean = supportsPassive ? {passive: false} : false;

/**
 * 카테고리(대분류)는 고정된 값이라 타이틀로 치환 가능.
 * 회차 메타 보여주는 부분에서 category title 이 넘어오지 않아 만들어둠
 */
export const CATEGORY_UID_TO_TITLE_MAP: {[key in CategoryTypeVo]: string} = {
  [CategoryTypeVo.Book]: '책',
  [CategoryTypeVo.None]: '',
  [CategoryTypeVo.KatalkNovel]: '채팅소설',
  [CategoryTypeVo.Cartoon]: '웹툰',
  [CategoryTypeVo.Novel]: '웹소설',
  [CategoryTypeVo.Movie]: '영화',
  [CategoryTypeVo.Broadcast]: '방송',
  [CategoryTypeVo.TalkDrip]: '톡드립',
};

/**
 * backstopJS 테스트에서 영역을 숨기기 위한 상수입니다.
 */
export const BackstopHideSelector = 'backstopHideSelector';

export const TEST_TIME_OUT_MS = 10000;

export const TEXT_RESTRICT_IN_PC =
  'PC에서는 이용할 수 없습니다\n카카오페이지 앱으로 확인해보세요';

export const ON_ISSUE_TITLE_MAP: {[key in OnIssue]: TextTKey} = {
  [OnIssue.End]: {
    text: '완결',
    textTKey: 'common_content_completed',
  },
  [OnIssue.Ing]: {
    text: '연재중',
    textTKey: 'library_content_ongoing',
  },
  [OnIssue.Pause]: {
    text: '휴재',
    textTKey: 'common_content_rest',
  },
  [OnIssue.Unknown]: {
    text: '-',
    // @ts-expect-error
    textTKey: '',
  },
};

/**
 * 계정 정보: https://kakaoent.atlassian.net/wiki/spaces/SVC/pages/3703013565
 */
export const RECAPTCHA_PUBLIC_KEY = '6LeowaorAAAAAMVW-o5SRQITOw6a7zZ4TKm0A53p';

export const KAKAO_PROXY = 'http://proxy.onkakao.net:3128';
export const GOOGLE_AUTHORIZE_URL =
  'https://accounts.google.com/o/oauth2/v2/auth';
export const GOOGLE_TOKEN_URL = 'https://oauth2.googleapis.com/token';
export const FACEBOOK_AUTHORIZE_URL =
  'https://www.facebook.com/v21.0/dialog/oauth';
export const FACEBOOK_TOKEN_URL =
  'https://graph.facebook.com/v21.0/oauth/access_token';
export const LINE_AUTHORIZE_URL =
  'https://access.line.me/oauth2/v2.1/authorize';
export const LINE_TOKEN_URL = 'https://api.line.me/oauth2/v2.1/token';
export const APPLE_AUTHORIZE_URL = 'https://appleid.apple.com/auth/authorize';

export const KAKAO_AUTHORIZE_URL = `${process.env.KAKAO_AUTH_API_ORIGIN}/oauth/authorize`;
export const KAKAO_TOKEN_URL = `${process.env.KAKAO_AUTH_API_ORIGIN}/oauth/token`;

/**
 * @todo(@harry-hi) 글로벌 이메일 input에서 사용하고 있습니다. 추후 zod.email로 validation을 변경하면 좋을 것 같습니다.
 * 해당 regex는 zod.email() 을 참고했습니다. (https://github.com/colinhacks/zod/blob/709dfe3d130ef696d3565f877326230a22b3e887/packages/zod/src/v3/types.ts#L617)
 * */
export const EMAIL_REGEX =
  /^(?!\.)(?!.*\.\.)([A-Z0-9_'+\-\.]*)[A-Z0-9_+-]@([A-Z0-9][A-Z0-9\-]*\.)+[A-Z]{2,}$/i;

export const ANDROID_ONEBUILD_WEBVIEW_BUILD_NUMBER = 26005536;
export const IOS_ONEBUILD_WEBVIEW_BUILD_VERSION = {
  major: 7,
  minor: 24,
  patch: 0,
};

export const SESSION_STORAGE_SPA_INITIAL_PATH = 'spa_initial_path';
export const SESSION_STORAGE_INITIAL_LOAD_MARKED = 'spa_initial_load_marked';

export const JWT_VERSION = 1.1;

/**
 * 이용권 만료 기본값 (72시간)
 */
export const DEFAULT_RENTAL_PERIOD_BY_MINUTE = 4320;

/**
 * 피쳐 오버라이드 쿠키 키
 * 값은 & 로 구분된 key=value (예: feature_override=feature1=true&feature2=false)
 */
export const COOKIE_KEY_OVERRIDE_FEATURE = 'feature_override';
